// SecurityDashboard — thin tab shell (wave 3: themed tabs, no WebTheme
// colours; the theme draws the selected tab).
//
// The heavy logic has been split into per-tab components under
// ./security/. Each child fetches its own RTDB slice on mount, so
// opening the dashboard no longer pays the cost of every tab's data, and
// a tab that is not open is not mounted (nothing here reads anything).
//
// The Overview tab is the default landing. Tile click-throughs there
// call `onNavigate(tabIndex)` to switch tabs in this shell, so the tab
// ORDER (the index of each tab) must stay as it is.
//
// Layout note: there is no ResponsiveDrawer wrapper here — the
// surrounding ProtectedRoute already provides it. There is also no
// page header or "always visible" stat cards — that responsibility
// lives in the Overview tab now. The admin one-shot tools that used to
// sit in a header button row (Flat-Rate All, Fill Vehicle Placeholders,
// Rebuild Customer Index) live in ./security/MaintenanceTools, together
// with the resumable backfill runners.

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import { EmptyState } from '../components/feedback';
import { tokens as c } from '../theme';
import SecurityOverview from './security/SecurityOverview';
import SecurityLogs from './security/SecurityLogs';
import StaffActivity from './StaffActivity';
import AgentPerformance from './security/AgentPerformance';
import ServiceOutages from './security/ServiceOutages';
import NotificationMetrics from './security/NotificationMetrics';
import RateLimits from './security/RateLimits';
import GeocodingApi from './security/GeocodingApi';
import DriverReset from './security/DriverReset';
import Landmarks from './security/Landmarks';
import SecuritySettings from './security/SecuritySettings';
import MaintenanceTools from './security/MaintenanceTools';
import SettingsWiki from './security/SettingsWiki';
import TruecallerPlayground from './security/TruecallerPlayground';

export default function SecurityDashboard() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(0); // 0 = Overview default
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth?.profile?.usertype === 'admin';

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  // The order is the tab index the Overview tiles navigate to (0–10); keep it.
  const tabs = [
    { key: 'overview', label: t('menu_group_overview', { defaultValue: 'Overview' }), icon: <DashboardOutlinedIcon /> },
    { key: 'logs', label: t('security_tab_logs', { defaultValue: 'Security Logs' }), icon: <SecurityOutlinedIcon /> },
    { key: 'staff', label: t('security_tab_staff', { defaultValue: 'Staff Activity' }), icon: <PersonOutlineRoundedIcon /> },
    { key: 'agents', label: t('agent_performance', { defaultValue: 'Agent Performance' }), icon: <AssessmentOutlinedIcon /> },
    { key: 'outages', label: t('security_tab_outages', { defaultValue: 'Service Outages' }), icon: <ReportProblemOutlinedIcon /> },
    { key: 'notifications', label: t('notifications', { defaultValue: 'Notifications' }), icon: <NotificationsActiveOutlinedIcon /> },
    { key: 'ratelimits', label: t('security_tab_rate_limits', { defaultValue: 'Rate Limits' }), icon: <SpeedOutlinedIcon /> },
    { key: 'geocoding', label: t('security_tab_geocoding', { defaultValue: 'Geocoding API' }), icon: <MapOutlinedIcon /> },
    { key: 'reset', label: t('security_tab_driver_reset', { defaultValue: 'Driver Reset' }), icon: <AutorenewRoundedIcon /> },
    { key: 'landmarks', label: t('security_tab_landmarks', { defaultValue: 'Landmarks' }), icon: <PlaceOutlinedIcon /> },
    { key: 'settings', label: t('settings_title', { defaultValue: 'Settings' }), icon: <LockOutlinedIcon /> },
    // Last on purpose: Overview tiles navigate by index 0–10.
    { key: 'maintenance', label: t('security_tab_maintenance', { defaultValue: 'Maintenance' }), icon: <BuildOutlinedIcon /> },
    { key: 'wiki', label: t('security_tab_settings_wiki', { defaultValue: 'Settings Wiki' }), icon: <MenuBookOutlinedIcon /> },
    // Playground for the caller-name lookup (truecaller_lookup CF).
    { key: 'truecaller', label: t('security_tab_truecaller', { defaultValue: 'Truecaller' }), icon: <ContactPhoneOutlinedIcon /> },
  ];

  return (
    <Box dir={i18n.dir()} sx={{ width: '100%', minWidth: 0 }}>
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={t('security_dashboard', { defaultValue: 'Security dashboard' })}
        sx={{ borderBottom: `1px solid ${c.OUTLINE}`, '& .MuiTab-root': { minHeight: 48 }, '& .MuiTab-iconWrapper': { fontSize: 18 } }}
      >
        {tabs.map((tab, i) => (
          <Tab
            key={tab.key}
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
            id={`security-tab-${i}`}
            aria-controls={`security-tabpanel-${i}`}
          />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`security-tabpanel-${activeTab}`} aria-labelledby={`security-tab-${activeTab}`} sx={{ pt: 2.5, minWidth: 0 }}>
        {activeTab === 0 && <SecurityOverview onNavigate={setActiveTab} />}
        {activeTab === 1 && <SecurityLogs />}
        {activeTab === 2 && <StaffActivity />}
        {activeTab === 3 && <AgentPerformance />}
        {activeTab === 4 && <ServiceOutages />}
        {activeTab === 5 && <NotificationMetrics />}
        {activeTab === 6 && <RateLimits />}
        {activeTab === 7 && <GeocodingApi />}
        {activeTab === 8 && <DriverReset />}
        {activeTab === 9 && <Landmarks />}
        {activeTab === 10 && <SecuritySettings />}
        {activeTab === 11 && <MaintenanceTools />}
        {activeTab === 12 && <SettingsWiki />}
        {activeTab === 13 && <TruecallerPlayground />}
      </Box>
    </Box>
  );
}
